import { formatResponse } from '../../../utils/StoreFilterList';
import {
  createFilterAPI,
  getFilterSettingAPI,
  updateFilterAPI,
  deleteFilterAPI,
  CreateFilterPayload,
  UpdateFilterPayload,
  UpdateOrderField,
} from '../../../external/StoreFilterApi';
import { storeFilterActions, modalConfirmActions } from '../../modules/Actions';
import { FilterItem } from '../../reducers/StoreFilterReducer';
import { AppState } from '../../modules/store';
import { Dispatch, ActionCreator, AnyAction } from 'redux';
import { isAxiosError } from '../../../external/Common';
import { ThunkAction } from 'redux-thunk';
import { AgencyInfo } from '../../reducers/StoreSelectorReducer';

const API_RESULT_CODE = {
  success: '0000',
  fail: '400',
};

const SORT_FIELDS = ['kanaName', 'longName', 'shortName', 'companyCfc'] as const;

// 店舗フィルタ一覧取得API
const getFilterList: ActionCreator<ThunkAction<void, AppState, unknown, AnyAction>> =
  (isAgency: boolean) => async (dispatch: Dispatch, getState: () => AppState) => {
    dispatch(storeFilterActions.getFilterSetting.start());
    try {
      const response = await getFilterSettingAPI();
      const { data } = response;
      const { filterStoreCds = [] } = data;
      const { resultCd = '' } = data.result || {};
      const state = getState();
      const agencies = isAgency ? state.loginUser.stores : sortAgencies(state.storeSelector.agencies);
      const formatFilter = formatResponse(filterStoreCds, agencies || []);
      switch (resultCd) {
        case API_RESULT_CODE.success:
          dispatch(storeFilterActions.getFilterSetting.end(formatFilter));
          break;
        default:
          dispatch(storeFilterActions.draftFilter.rollBack());
          break;
      }
    } catch (error) {
      if (isAxiosError(error)) {
        const { resultMessageDtoList = [] } = error?.response?.data?.result || {};
        if (resultMessageDtoList.length) {
          dispatch(storeFilterActions.getFilterSetting.fail(resultMessageDtoList));
        }
        dispatch(storeFilterActions.draftFilter.rollBack());
      }
    } finally {
      dispatch(modalConfirmActions.handleLoading(false));
    }
  };

// 店舗フィルタ編集API
const updateFilter: ActionCreator<ThunkAction<void, AppState, unknown, AnyAction>> =
  (item: FilterItem) => async (dispatch: Dispatch, getState: () => AppState) => {
    if (!item.ordcstmCdList.length) {
      return;
    }
    dispatch(storeFilterActions.updateFilterSetting.start());
    dispatch(modalConfirmActions.handleLoading(true));
    try {
      const { filterStore } = getState();
      const updateType = checkUpdateType(filterStore.isDraft, filterStore.isDraftOrder);

      const orderFilterList: UpdateOrderField[] = formatOrderFilterList(filterStore.data);

      const payload: UpdateFilterPayload = {
        storeFilterUpdateRequest: {
          filterId: item.filterID,
          filterName: item.filterName,
          ordcstmCdList: item.ordcstmCdList,
          sortOrder: item.sortOrder,
        },
        storeFilterOrderRequestList: orderFilterList,
        updateType,
      };

      const { data } = await updateFilterAPI(payload);
      const { resultCd = '' } = data?.result || {};
      switch (resultCd) {
        case API_RESULT_CODE.success:
          if (!item.isExist) {
            dispatch(storeFilterActions.updateFilterSetting.exist(item));
          }
          dispatch(storeFilterActions.updateFilterSetting.end());
          break;
        default:
          dispatch(storeFilterActions.draftFilter.rollBack());
          break;
      }
    } catch (error) {
      if (isAxiosError(error)) {
        const { resultMessageDtoList = [] } = error?.response?.data?.result || {};
        if (resultMessageDtoList.length) {
          dispatch(storeFilterActions.updateFilterSetting.fail(resultMessageDtoList));
        }
        dispatch(storeFilterActions.draftFilter.rollBack());
      }
    } finally {
      dispatch(modalConfirmActions.handleLoading(false));
    }
  };

// 店舗フィルタ作成API
const createFilter: ActionCreator<ThunkAction<void, AppState, unknown, AnyAction>> =
  (payload: CreateFilterPayload, isAgency = false) =>
  async (dispatch: any) => {
    dispatch(storeFilterActions.createFilterSetting.start());
    dispatch(modalConfirmActions.handleLoading(true));
    try {
      const { data } = await createFilterAPI(payload);
      const { resultCd = '' } = data?.result || {};

      switch (resultCd) {
        case API_RESULT_CODE.success:
          dispatch(getFilterList(isAgency));
          break;
        default:
          dispatch(storeFilterActions.draftFilter.rollBack());
          break;
      }
    } catch (error) {
      if (isAxiosError(error)) {
        const { resultMessageDtoList = [] } = error?.response?.data?.result || {};
        if (resultMessageDtoList.length) {
          dispatch(storeFilterActions.createFilterSetting.fail(resultMessageDtoList));
        }
        dispatch(storeFilterActions.draftFilter.rollBack());
      }
    } finally {
      dispatch(modalConfirmActions.handleLoading(false));
    }
  };

// 店舗フィルタ削除API
const deleteFilter: ActionCreator<ThunkAction<void, AppState, unknown, AnyAction>> =
  (payload: FilterItem) => async (dispatch: Dispatch) => {
    dispatch(storeFilterActions.deleteFilter.start());
    dispatch(modalConfirmActions.handleLoading(true));
    try {
      const { data } = await deleteFilterAPI(payload.filterID);
      const { resultCd = '' } = data?.result || {};
      switch (resultCd) {
        case API_RESULT_CODE.success:
          dispatch(storeFilterActions.deleteFilter.end(payload));
          break;
        default:
          dispatch(storeFilterActions.draftFilter.rollBack());
          break;
      }
    } catch (error) {
      if (isAxiosError(error)) {
        const { resultMessageDtoList = [] } = error?.response?.data?.result || {};
        if (resultMessageDtoList.length) {
          dispatch(storeFilterActions.deleteFilter.fail(resultMessageDtoList));
        }
        dispatch(storeFilterActions.draftFilter.rollBack());
      }
    } finally {
      dispatch(modalConfirmActions.handleLoading(false));
    }
  };

function sortAgencies(agencies?: { [key: string]: AgencyInfo }) {
  return Object.values(agencies || {}).sort((a, b) => {
    for (const sortField of SORT_FIELDS) {
      const compare = String(a[sortField]).localeCompare(String(b[sortField]), 'ja');
      if (compare !== 0) return compare;
    }
    return a.agencyCd.localeCompare(b.agencyCd, 'ja');
  });
}

function formatOrderFilterList(data: FilterItem[]) {
  return data.map((ele, index) => ({ filterId: ele.filterID, sortOrder: index + 1 }));
}

function checkUpdateType(isDraft: boolean, isDraftOrder: boolean): number {
  const UPDATE_TYPE = {
    all: 0,
    settings: 1,
    order: 2,
  };
  let updateType = UPDATE_TYPE.settings;

  if (isDraftOrder) {
    updateType = UPDATE_TYPE.order;
  }

  if (isDraft && isDraftOrder) {
    updateType = UPDATE_TYPE.all;
  }
  return updateType;
}

export { getFilterList, updateFilter, createFilter, deleteFilter, sortAgencies, formatOrderFilterList, checkUpdateType };
